// MidsPanel — the Merchant services section (account-area redesign Task 9,
// per the plan's 2026-10-02 amendment F9/F10; rulings O4/O10). Mounted by
// AccountCenter, whose shell already paints the section's registry title
// ("Merchant services") and description above this panel — this component
// deliberately renders NO page header of its own (the Task 5 divergence:
// a second header would duplicate the shell's).
//
// ⚠️ AMENDED 2026-10-04 (account-area redesign, Task 9). ORIGINAL (TD-384
// T4): a "Processor MIDs" card + an inline "Claim a MID" form with
// processor/DBA/opened fields, and a local StatusPill/statusColor pair.
// CURRENT (the amendment): the page copy speaks of merchant services
// accounts, never "MID"/processor/DBA; the table is Merchant account
// number / Store / Status (O10 removed Processor, DBA and Opened — so no
// date renders here and the old R1 calendar-safe-date acceptance line is
// void); statuses badge through EmbedStatusBadge domain="mid" (the one
// status map, embed-shared/status.ts); claiming is a "Request a link"
// dialog.
//
// ⚠️ AMENDED 2026-10-07 (account-area redesign, Task 19; operator feedback
// F9, ruling S3). The request is now a LINK REQUEST against migration 146:
//
//   - The dialog collects the account number, an optional Store picker, and
//     the location snapshot (Store name / Street / City / State / ZIP).
//     Choosing a store PREFILLS the snapshot from the store record (O10:
//     overwrites — the store's address is the truth) and the fields stay
//     editable; "None" leaves typed values alone. Store name + ZIP are
//     required when NO store is chosen (the server's 422 contract,
//     validated here first so the customer sees per-field errors).
//   - The endpoint answers a STATUS ENVELOPE, never a row (mid-api.ts
//     ClaimMIDResult), so rows are no longer seeded from the mutation
//     response (the 2026-10-04 §59 seeding is retired with the row-shaped
//     response). A "queued" answer re-reads the list so a created row
//     renders (an own-pending repeat and a cloaked conflict insert
//     nothing). Under ruling S3 a customer request can never complete a
//     link — a fresh row is PENDING, awaiting operator verification — so
//     every queued outcome renders the review copy; the derived "Linked"
//     banner was retired by the gate mapping fix (2026-10-08): it claimed a
//     link the operator's verify had not granted. "Already linked"
//     comes verbatim from the server's own answer (the number matched the
//     caller's OWN live account — ruling S3: the only "match" that is ever
//     revealed; another customer's live number queues with the existence
//     cloaked, OWASP A01).
//   - The success banner states exactly one of the two strings below
//     (brief: exact copy — they ride the OPERATOR-REVIEW list).
//
// ⚠️ AMENDED 2026-10-08 (Task 19 gate micro round, concern 3). The list has
// TWO writers that can race: load() (mount, Retry) and handleSubmit's
// post-submit refetch. Without sequencing, last-write-wins: an INITIAL load
// held past the submit-refetch settles afterwards and its setMids wiped the
// just-refetched rows — the new row vanished from the table (the panel even
// fell back to the empty state) while the banner still told the truth. Both
// writers now share one sequence token (midsListSeqRef, the Task 18
// loadOrdersSeqRef pattern): a superseded load() completion applies NONE of
// its state — the loading flag included, so the newer writer owns it; and
// the refetch bumps the token before its writes, superseding any in-flight
// load and taking that ownership itself.
//
// What a row IS is unchanged: a processor-assigned settlement account the
// customer asks Aldelo to link to their referral savings. The merchant
// requests the link here (created rows are born pending, version 1); an
// operator verifies it on the portal side (MidsAdminPage) — no account
// becomes active without a named operator (the credit-theft control,
// design §3). The merchant cannot verify/close/reject their own requests,
// so this panel is list + one request dialog and NOTHING mutates an
// existing row (hence no If-Match anywhere here).
//
// The number is TEXT end-to-end (design §3: these account numbers are
// digit strings and may carry leading zeros — IRIS types merchantNumber as
// integer; we deliberately do not follow it). No numeric input, no
// Number() coercion, no formatting on display.
//
// Blank optionals are OMITTED from the body, never sent as "" (the binding
// client constraint; mid-api.ts trims and omits).
//
// The request form's field state lives HERE, not inside the dialog, on
// purpose: EmbedDialog unmounts its children on close, and a customer who
// cancels (or fat-fingers Escape) must find their half-typed number still
// there when they reopen. Success clears it — the request exists now.
//
// Store association is an OPTIONAL picker over the customer's own stores
// (store-api.ts listStores — an account is *associated with* a store, never
// owned by one); a failed stores read degrades the picker to "—" per row
// and never takes the list down (allSettled, the ProductsPanel convention).
//
// Inline styles only — no Tailwind (embed convention). Every <button> is
// explicitly type="button"/type="submit" (EmbedButton does it at birth,
// project rule §73).
//
// ⚠️ AMENDED 2026-10-09 (account-area redesign, Task 21 BASELINE). The
// request dialog's location-snapshot inputs carry WHATWG autofill tokens
// (street-address on the single combined street field — migration 146 has
// ONE requested_line1, no line2 — plus address-level2 city, address-level1
// state, postal-code ZIP) and `name` attributes mirroring their wire keys.
// Attributes only; values, validation and the wire shape are unchanged. The
// Store picker and the account number are not address inputs and carry no
// address token; "Store name" is a business name with no token in the
// address set.

import { useState, useEffect, useCallback, useRef, type FormEvent } from "react";
import {
  useEmbedTokens,
  EmbedTable,
  EmbedDialog,
  useEmbedDialog,
  EmbedStatusBadge,
  EmbedEmptyState,
  EmbedBanner,
  EmbedButton,
  EmbedCard,
  EmbedField,
  EmbedInput,
  EmbedSelect,
} from "../../embed-shared/primitives";
import { ApiError } from "../../embed-shared/api-client";
import { listMyMIDs, claimMID } from "./mid-api";
import type { MerchantMIDWire } from "./mid-api";
import { listStores } from "./store-api";
import type { CustomerStoreWire } from "./store-api";

// The page's hint, the amendment's draft — OPERATOR-REVIEW COPY (it rides
// the merge-review list). One sentence per part: what the account is, then
// what to do when one is missing. No "MID"/processor/DBA jargon.
const HINT =
  "Your merchant services account is how Aldelo applies referral savings to " +
  "your processing statement. Aldelo links your accounts for you — if one is " +
  "missing, request a link.";

// The success states (Task 19 brief: EXACT strings — operator-review copy).
// "Already linked" is the server's own authoritative answer; every queued
// request shows the review copy — under ruling S3 a customer request can
// never complete a link, so no queued outcome may claim "Linked".
const ALREADY_LINKED_COPY = "Already linked";
const SENT_FOR_REVIEW_COPY = "Sent to Aldelo for review";

const EM_DASH = "—";

interface RequestFields {
  mid_number: string;
  store_id: string;
  // The migration-146 location snapshot — prefilled from the chosen store
  // (O10), editable, and required (name + ZIP) when no store is chosen.
  requested_store_name: string;
  requested_line1: string;
  requested_city: string;
  requested_state: string;
  requested_postal_code: string;
}

const EMPTY_REQUEST: RequestFields = {
  mid_number: "",
  store_id: "",
  requested_store_name: "",
  requested_line1: "",
  requested_city: "",
  requested_state: "",
  requested_postal_code: "",
};

export function MidsPanel() {
  const t = useEmbedTokens();
  const dialog = useEmbedDialog();

  const [mids, setMids] = useState<MerchantMIDWire[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [stores, setStores] = useState<CustomerStoreWire[]>([]);

  const [fields, setFields] = useState<RequestFields>(EMPTY_REQUEST);
  const [numberError, setNumberError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [zipError, setZipError] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  // The success banner's exact string, or null when hidden. Replaces Task
  // 9's boolean requestSent: the STATE is which of the three copies applied.
  const [successCopy, setSuccessCopy] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const messageOf = useCallback((reason: unknown, fallback: string): string => {
    return reason instanceof ApiError ? reason.message : fallback;
  }, []);

  // In-flight guard (micro round 2026-10-08; the Task 18 loadOrdersSeqRef
  // pattern, header note above): load() callers overlap (mount vs Retry) and
  // handleSubmit's refetch is a THIRD writer of the list state that can land
  // while a load() is still in flight. Each load takes a token; a superseded
  // completion applies NONE of its state — the loading flag included, so the
  // newer writer owns it until IT settles. The refetch rides the same token
  // (see handleSubmit).
  const midsListSeqRef = useRef(0);
  const load = useCallback(async () => {
    const seq = ++midsListSeqRef.current;
    setLoading(true);
    setLoadError(null);
    // allSettled (the ProductsPanel convention): a failed stores fetch must
    // not take the account list down with it — requesting a link without a
    // store association is fully functional, so stores degrade to a "—"
    // in the Store column.
    const [midsR, storesR] = await Promise.allSettled([listMyMIDs(), listStores()]);
    if (seq !== midsListSeqRef.current) return;
    if (midsR.status === "fulfilled") setMids(midsR.value);
    else setLoadError(messageOf(midsR.reason, "Failed to load your merchant accounts"));
    if (storesR.status === "fulfilled") setStores(storesR.value);
    setLoading(false);
  }, [messageOf]);

  useEffect(() => {
    load();
  }, [load]);

  const updateField = useCallback(
    (key: keyof RequestFields, value: string) => {
      setFields((prev) => ({ ...prev, [key]: value }));
      // Per-field errors clear the moment their own field changes; the
      // request error and a settled success band clear on ANY edit.
      if (key === "mid_number" && numberError) setNumberError(null);
      if (key === "requested_store_name" && nameError) setNameError(null);
      if (key === "requested_postal_code" && zipError) setZipError(null);
      if (requestError) setRequestError(null);
      if (successCopy !== null) setSuccessCopy(null);
    },
    [numberError, nameError, zipError, requestError, successCopy],
  );

  // Store selection (O10 prefill): choosing a store copies its location
  // over the snapshot fields — an OVERWRITE, because the saved store's
  // address is the authoritative one and a stale hand-typed draft must not
  // survive the choice; the fields stay editable for corrections. "None"
  // touches nothing, so typed values survive a de-selection.
  const handleStoreChange = useCallback(
    (value: string) => {
      setFields((prev) => {
        if (value === "") return { ...prev, store_id: "" };
        const chosen = stores.find((s) => s.id === value);
        if (!chosen) return { ...prev, store_id: value };
        return {
          ...prev,
          store_id: value,
          requested_store_name: chosen.name,
          requested_line1: chosen.line1 ?? "",
          requested_city: chosen.city ?? "",
          requested_state: chosen.state ?? "",
          requested_postal_code: chosen.postal_code ?? "",
        };
      });
      // The prefill resolves the "required" errors by construction.
      if (nameError) setNameError(null);
      if (zipError) setZipError(null);
      if (requestError) setRequestError(null);
      if (successCopy !== null) setSuccessCopy(null);
    },
    [stores, nameError, zipError, requestError, successCopy],
  );

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!fields.mid_number.trim()) {
        setNumberError("Merchant account number is required.");
        return;
      }
      // The server's 422 contract, mirrored client-side: the location
      // snapshot is required (name + ZIP) only when NO store is chosen —
      // with a store chosen the association itself carries the location.
      if (fields.store_id === "") {
        const missingName = !fields.requested_store_name.trim();
        const missingZip = !fields.requested_postal_code.trim();
        if (missingName || missingZip) {
          if (missingName) setNameError("Store name is required.");
          if (missingZip) setZipError("ZIP code is required.");
          return;
        }
      }
      setSubmitting(true);
      setRequestError(null);
      setSuccessCopy(null);
      try {
        const result = await claimMID({
          mid_number: fields.mid_number,
          store_id: fields.store_id || undefined,
          requested_store_name: fields.requested_store_name,
          requested_line1: fields.requested_line1,
          requested_city: fields.requested_city,
          requested_state: fields.requested_state,
          requested_postal_code: fields.requested_postal_code,
        });

        if (result.outcome === "already_linked") {
          // The server compared against the database and found the caller's
          // OWN live account — authoritative regardless of local list state,
          // so the BANNER never waits on a confirming read. The table is a
          // different matter (H-1, review M1's twin outcome): a stale
          // load-error card must not sit under a success banner (the table
          // is gated on !loadError), and a list that FAILED to deliver is
          // not the customer's real list — the linked row exists server-side
          // and must render. When the pre-submit list settled cleanly the
          // row is already in it (GET /me/mids returns all statuses), so the
          // no-refetch pin holds; only a failed read triggers the re-read,
          // with the same guarded writes the queued refetch uses (token
          // bumped, loading flag taken — the guard's contract above).
          setSuccessCopy(ALREADY_LINKED_COPY);
          if (loadError !== null) {
            try {
              const fresh = await listMyMIDs();
              midsListSeqRef.current++;
              setMids(fresh);
              setLoadError(null);
              setLoading(false);
            } catch {
              // The re-read failed: the error card stays (honest about the
              // list), the banner keeps the server's authoritative answer.
            }
          }
        } else {
          // queued — the request either created a row or joined an existing
          // one (own pending again, or a cloaked conflict; neither inserts).
          // Re-read the list so a created row renders in the table. Under
          // ruling S3 a customer request can never complete a link: a fresh
          // row is PENDING, awaiting operator verification — so the banner is
          // the review copy; "Linked" would claim a link the operator's
          // verify has not granted.
          try {
            const fresh = await listMyMIDs();
            // The refetch supersedes any in-flight load(): token bumped so the
            // stale completion applies NOTHING, and the loading flag cleared
            // because THIS write is the newest — a held load would otherwise
            // leave loading stuck true (and the table is gated on it). Only
            // the success path bumps: a failed refetch must not invalidate a
            // load that is still coming to own the flag.
            midsListSeqRef.current++;
            setMids(fresh);
            setLoadError(null);
            setLoading(false);
            setSuccessCopy(SENT_FOR_REVIEW_COPY);
          } catch {
            // The POST itself succeeded; only the confirming read failed.
            // Keep the table's last good contents — the banner stays the
            // review copy, the list is simply not refreshed.
            setSuccessCopy(SENT_FOR_REVIEW_COPY);
          }
        }
        setFields(EMPTY_REQUEST);
        dialog.close();
      } catch (err) {
        // Inside the dialog, verbatim (the server message is the contract),
        // values intact so the customer can correct them. ⚠️ The one string
        // painted here that this panel does NOT own: the live 409 backstop
        // (customer_handler_mid.go:153) reads "This merchant account number
        // is already being processed; contact support if that is
        // unexpected" — it fires only on a race (a live number is normally
        // answered as queued with the conflict cloaked, ruling S3), and its
        // wording rides the OPERATOR-REVIEW list with the hint and the
        // success copies.
        setRequestError(messageOf(err, "Failed to send your request. Please try again."));
      } finally {
        setSubmitting(false);
      }
    },
    // fields/loadError are read inside: the fields are the request body and
    // loadError is H-1's re-read trigger (a stale closure would miss or
    // force the already_linked re-read). The mids/loading reads the retired
    // baseline diff had are gone with it (2026-10-08 gate round 3).
    [fields, loadError, dialog, messageOf],
  );

  const storeNameById = new Map(stores.map((s) => [s.id, s.name]));
  const showEmptyState = !loading && !loadError && mids.length === 0;

  // Review P2 (focus): in the empty state the ONLY "Request a link" trigger
  // is the empty state's action, and a first-ever success unmounts it in the
  // same React commit that closes the dialog — EmbedDialog's focus restore
  // skips the then-detached target (React commits deletions, including the
  // dialog's cleanup, before the band button's placement, so even a
  // redirected returnFocusTo is not yet connected there) and focus would
  // drop to <body>. The band's button mounts in that same commit as the
  // replacement trigger: focus it from this post-commit effect. When the
  // band's own button opened the dialog it is still connected and
  // EmbedDialog's restore already handled it — the detached check leaves
  // that path alone.
  const bandRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (successCopy !== null && dialog.returnFocusTo !== null && !dialog.returnFocusTo.isConnected) {
      bandRef.current?.querySelector("button")?.focus();
    }
  }, [successCopy, dialog.returnFocusTo]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.md }}>
      {/* The page's action band: the hint beside the primary action — the
          plan's "header action", placed in the panel because the shell owns
          the page header (the Task 5 divergence). The empty state below
          carries its own "Request a link", so the band's button yields
          while the empty state is showing: exactly one trigger is visible
          in every settled state. */}
      <div
        ref={bandRef}
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: t.spacing.md,
        }}
      >
        <p
          style={{
            margin: 0,
            minWidth: 0,
            maxWidth: 640,
            color: t.colors.textSecondary,
            fontSize: t.fontSize.sm,
            lineHeight: t.lineHeight.normal,
          }}
        >
          {HINT}
        </p>
        {!showEmptyState && (
          <EmbedButton
            type="button"
            variant="primary"
            onClick={(e) => {
              setRequestError(null);
              dialog.openFrom(e.currentTarget);
            }}
          >
            Request a link
          </EmbedButton>
        )}
      </div>

      {successCopy !== null && (
        // EmbedBanner(success) carries role="status" (polite) — the banner
        // lives at page level because the dialog closes on success. The
        // string is one of the three exact success states.
        <EmbedBanner tone="success">{successCopy}</EmbedBanner>
      )}

      {loadError && (
        // EmbedBanner(error) already carries role="alert" — no wrapper
        // role here (a second one makes getByRole("alert") ambiguous).
        <EmbedCard>
          <EmbedBanner tone="error">{loadError}</EmbedBanner>
          <div style={{ marginTop: t.spacing.md }}>
            <EmbedButton type="button" variant="secondary" onClick={load}>
              Retry
            </EmbedButton>
          </div>
        </EmbedCard>
      )}

      {loading && (
        <p role="status" style={{ margin: 0 }}>
          Loading your merchant accounts...
        </p>
      )}

      {showEmptyState && (
        <EmbedEmptyState
          icon="mids"
          title="No merchant accounts linked yet."
          action={
            <EmbedButton
              type="button"
              variant="primary"
              onClick={(e) => {
                setRequestError(null);
                dialog.openFrom(e.currentTarget);
              }}
            >
              Request a link
            </EmbedButton>
          }
        />
      )}

      {!loading && !loadError && mids.length > 0 && (
        <EmbedTable<MerchantMIDWire>
          caption="Your merchant accounts"
          rows={mids}
          rowKey={(mid) => mid.id}
          rowTestId={(mid) => `mid-row-${mid.id}`}
          columns={[
            {
              id: "number",
              header: "Merchant account number",
              primary: true,
              cell: (mid) => (
                <span
                  style={{
                    fontFamily: t.fontFamilyMono,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {mid.mid_number}
                </span>
              ),
            },
            {
              id: "store",
              header: "Store",
              cell: (mid) => (
                <span>{mid.store_id ? (storeNameById.get(mid.store_id) ?? EM_DASH) : EM_DASH}</span>
              ),
            },
            {
              id: "status",
              header: "Status",
              cell: (mid) => <EmbedStatusBadge domain="mid" value={mid.status} />,
            },
          ]}
        />
      )}

      {/* Request a link — the dialog owns none of the form's state (see the
          file header), so a cancel leaves the draft in place and a success
          closes with the success band derived from the envelope + refetch.
          closeDisabled pins Escape and the backdrop while the POST is in
          flight. Size "medium": six fields no longer fit the small shell. */}
      <EmbedDialog
        open={dialog.open}
        onClose={dialog.close}
        returnFocusTo={dialog.returnFocusTo}
        title="Request a link"
        closeDisabled={submitting}
        size="medium"
      >
        {requestError && (
          <div style={{ marginBottom: t.spacing.md }}>
            <EmbedBanner tone="error">{requestError}</EmbedBanner>
          </div>
        )}
        <form onSubmit={handleSubmit} noValidate>
          <EmbedField
            label="Merchant account number"
            htmlFor="request-account-number"
            required
            error={numberError ?? undefined}
            help="Enter the number exactly as it appears on your statement — leading zeros matter."
          >
            <EmbedInput
              id="request-account-number"
              type="text"
              value={fields.mid_number}
              onChange={(e) => updateField("mid_number", e.target.value)}
              aria-required="true"
              invalid={!!numberError}
              aria-invalid={!!numberError}
            />
          </EmbedField>

          <EmbedField
            label="Store"
            htmlFor="request-store"
            help="Optional — pick one of your saved locations and we fill in the details below."
          >
            <EmbedSelect
              id="request-store"
              value={fields.store_id}
              onChange={(e) => handleStoreChange(e.target.value)}
            >
              <option value="">None</option>
              {stores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </EmbedSelect>
          </EmbedField>

          {/* The location snapshot (migration 146). Prefilled by the store
              picker above, always editable; name + ZIP become required the
              moment no store is chosen (mirrors the server's 422). */}
          <EmbedField
            label="Store name"
            htmlFor="request-store-name"
            required={!fields.store_id}
            error={nameError ?? undefined}
          >
            <EmbedInput
              id="request-store-name"
              type="text"
              value={fields.requested_store_name}
              onChange={(e) => updateField("requested_store_name", e.target.value)}
              invalid={!!nameError}
              aria-invalid={!!nameError}
            />
          </EmbedField>

          <EmbedField label="Street address" htmlFor="request-store-line1">
            <EmbedInput
              id="request-store-line1"
              name="requested_line1"
              autoComplete="street-address"
              type="text"
              value={fields.requested_line1}
              onChange={(e) => updateField("requested_line1", e.target.value)}
            />
          </EmbedField>

          <EmbedField label="City" htmlFor="request-store-city">
            <EmbedInput
              id="request-store-city"
              name="requested_city"
              autoComplete="address-level2"
              type="text"
              value={fields.requested_city}
              onChange={(e) => updateField("requested_city", e.target.value)}
            />
          </EmbedField>

          <EmbedField label="State" htmlFor="request-store-state">
            <EmbedInput
              id="request-store-state"
              name="requested_state"
              autoComplete="address-level1"
              type="text"
              value={fields.requested_state}
              onChange={(e) => updateField("requested_state", e.target.value)}
            />
          </EmbedField>

          <EmbedField
            label="ZIP code"
            htmlFor="request-store-zip"
            required={!fields.store_id}
            error={zipError ?? undefined}
          >
            <EmbedInput
              id="request-store-zip"
              name="requested_postal_code"
              autoComplete="postal-code"
              type="text"
              value={fields.requested_postal_code}
              onChange={(e) => updateField("requested_postal_code", e.target.value)}
              invalid={!!zipError}
              aria-invalid={!!zipError}
            />
          </EmbedField>

          <div
            style={{
              marginTop: t.spacing.md,
              display: "flex",
              gap: t.spacing.md,
              justifyContent: "flex-end",
            }}
          >
            <EmbedButton type="button" variant="secondary" onClick={dialog.close} disabled={submitting}>
              Cancel
            </EmbedButton>
            <EmbedButton type="submit" variant="primary" disabled={submitting} loading={submitting}>
              {submitting ? "Sending..." : "Send request"}
            </EmbedButton>
          </div>
        </form>
      </EmbedDialog>
    </div>
  );
}
